import React, { useRef, useState } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { 
  Download, CheckCircle, Copy, Printer, Clock, AlertTriangle, ExternalLink
} from 'lucide-react';

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-600 dark:bg-blue-500', textColor: 'text-white', icon: Clock },
  partial: { label: 'Partial', color: 'bg-amber-600 dark:bg-amber-500', textColor: 'text-white', icon: AlertTriangle },
  paid: { label: 'Paid', color: 'bg-green-600 dark:bg-green-500', textColor: 'text-white', icon: CheckCircle },
};

const PAYMENT_METHODS = [
  { value: 'Check', label: 'Check' },
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Venmo', label: 'Venmo' },
  { value: 'CashApp', label: 'CashApp' },
  { value: 'Zelle', label: 'Zelle' },
  { value: 'Wire Transfer', label: 'Wire Transfer' },
  { value: 'ACH', label: 'ACH/Bank Transfer' },
  { value: 'Other', label: 'Other' },
];

export default function StatementViewDialog({ 
  open, 
  onOpenChange, 
  statement, 
  profile, 
  invoiceSettings,
  onAction 
}) {
  const printRef = useRef(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  if (!statement) return null;

  const StatusIcon = statusConfig[statement.status]?.icon || Clock;

  // Branding - exactly like InvoiceViewDialog
  const brandingName = invoiceSettings?.company_name || profile?.company_name || profile?.display_name || 'Process Server';
  const brandingTagline = invoiceSettings?.tagline || '';
  const brandingLogo = invoiceSettings?.logo_url || null;
  const logoSize = invoiceSettings?.logo_size || 'medium';
  const footerPhone = invoiceSettings?.footer_phone || profile?.phone || '';
  const footerEmail = invoiceSettings?.footer_email || profile?.email || '';
  const footerWebsite = invoiceSettings?.footer_website || profile?.website || '';
  const footerText = invoiceSettings?.footer_text || '';

  // Theme settings - exactly like InvoiceViewDialog
  const theme = invoiceSettings?.theme || {};
  const primaryColor = theme.primary_color || '#1e40af';
  const accentColor = theme.accent_color || '#3b82f6';
  const headerBgColor = theme.header_bg_color || '#f8fafc';
  const textColor = theme.text_color || '#1e293b';
  const fontFamily = theme.font_family || 'system';

  // Font family mapping
  const fontFamilyMap = {
    'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'inter': '"Inter", sans-serif',
    'roboto': '"Roboto", sans-serif',
    'open-sans': '"Open Sans", sans-serif',
    'lato': '"Lato", sans-serif',
    'poppins': '"Poppins", sans-serif',
    'playfair': '"Playfair Display", serif',
    'merriweather': '"Merriweather", serif',
    'georgia': 'Georgia, serif',
    'times': '"Times New Roman", Times, serif',
  };
  const fontFamilyStyle = fontFamilyMap[fontFamily] || fontFamilyMap['system'];

  // Logo size classes - matching InvoiceViewDialog
  const logoSizeClasses = {
    small: 'h-16 w-auto max-w-[120px]',
    medium: 'h-24 w-auto max-w-[200px]',
    large: 'h-32 w-auto max-w-[300px]',
    xlarge: 'h-48 w-auto max-w-[500px]'
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsRecordingPayment(true);
    try {
      await api.recordStatementPayment(statement.id, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        notes: paymentNotes || null
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setPaymentAmount('');
      setPaymentMethod('Check');
      setPaymentNotes('');
      onAction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/statement/${statement.id}`;
    navigator.clipboard.writeText(link);
    toast.success('Statement link copied to clipboard');
  };

  const generatePrintHTML = () => {
    // Logo size mapping for print
    const printLogoSizes = {
      small: '60px',
      medium: '90px', 
      large: '120px',
      xlarge: '160px'
    };
    const printLogoHeight = printLogoSizes[logoSize] || printLogoSizes.medium;

    const invoicesHTML = statement.invoice_summaries?.map(inv => `
      <tr>
        <td><a href="${window.location.origin}/invoice/${inv.invoice_id}" style="color: ${accentColor}; text-decoration: none;">${inv.invoice_number}</a></td>
        <td style="text-align: center;">${formatDateShort(inv.invoice_date)}</td>
        <td style="text-align: center;">${formatDateShort(inv.due_date)}</td>
        <td style="text-align: right;">${formatCurrency(inv.total)}</td>
        <td style="text-align: right;">${formatCurrency(inv.total_paid)}</td>
        <td style="text-align: right; font-weight: ${inv.balance_due > 0 ? 'bold' : 'normal'}; color: ${inv.balance_due <= 0 ? '#16a34a' : 'inherit'};">
          ${inv.balance_due <= 0 ? 'Paid' : formatCurrency(inv.balance_due)}
        </td>
      </tr>
    `).join('') || '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Statement ${statement.statement_number}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: ${fontFamilyStyle}; color: ${textColor}; font-size: 11px; }
          .statement { max-width: 800px; margin: 0 auto; background: white; }
          
          .header { text-align: center; padding: 12px; margin: 8px 28px 0; background: ${headerBgColor}; border-radius: 6px; display: flex; flex-direction: column; align-items: center; }
          .logo { height: ${printLogoHeight}; max-width: 280px; object-fit: contain; display: block; margin: 0 auto; }
          .company-name { font-size: 18px; font-weight: bold; margin-bottom: 2px; color: ${primaryColor}; }
          .tagline { color: ${textColor}; opacity: 0.6; font-size: 9px; margin-top: 3px; }
          
          .meta-wrapper { padding: 6px 28px; }
          .meta-table { width: 100%; border-collapse: collapse; background: ${headerBgColor}; border: 1px solid ${accentColor}40; border-radius: 6px; }
          .meta-table td { padding: 8px 6px; text-align: center; border-left: 1px solid ${accentColor}40; vertical-align: middle; }
          .meta-table td:first-child { border-left: none; }
          .meta-label { font-size: 8px; color: ${textColor}; opacity: 0.5; text-transform: uppercase; letter-spacing: 0.3px; display: block; }
          .meta-value { font-size: 10px; font-weight: 600; margin-top: 2px; display: block; }
          
          .content { padding: 12px 28px; }
          .bill-to { margin-bottom: 16px; }
          .bill-to-label { font-size: 9px; color: ${textColor}; opacity: 0.5; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
          .bill-to-name { font-weight: 600; font-size: 12px; }
          .bill-to-detail { font-size: 10px; opacity: 0.6; }
          
          .section-title { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid ${primaryColor}; padding-bottom: 3px; margin-bottom: 0; color: ${primaryColor}; }
          .invoices-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
          .invoices-table th { text-align: left; padding: 3px 4px; font-size: 8px; color: ${textColor}; opacity: 0.5; text-transform: uppercase; font-weight: 500; border-bottom: 1px solid ${accentColor}40; }
          .invoices-table td { padding: 6px 4px; border-bottom: 1px solid ${accentColor}20; font-size: 10px; }
          
          .totals-wrapper { display: flex; justify-content: flex-end; margin-top: 12px; padding-top: 8px; border-top: 1px solid ${accentColor}40; }
          .totals-section { width: 200px; }
          .total-row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 10px; opacity: 0.7; }
          .total-row.final { font-weight: bold; font-size: 12px; opacity: 1; padding-top: 6px; border-top: 2px solid ${primaryColor}; margin-top: 4px; }
          .total-row.final .amount { color: ${primaryColor}; }
          
          .payments-section { margin-top: 12px; padding-top: 8px; border-top: 1px solid ${accentColor}40; }
          .payments-header { font-size: 8px; color: ${textColor}; opacity: 0.5; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
          .payment-row { font-size: 10px; padding: 2px 0; display: flex; justify-content: space-between; gap: 12px; background: #f0fdf4; padding: 4px 8px; border-radius: 4px; margin-bottom: 2px; }
          .payment-row .amount { color: #16a34a; font-weight: 500; }
          
          .footer { background: ${headerBgColor}; padding: 8px 14px; margin: 8px 28px 8px; border-radius: 6px; }
          .footer-contact { display: flex; justify-content: center; gap: 16px; font-size: 10px; opacity: 0.7; margin-bottom: 3px; }
          .footer-text { font-size: 10px; opacity: 0.5; text-align: center; }
          
          @media print {
            @page { size: letter; margin: 0.25in; }
            body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .statement { max-width: none; }
          }
        </style>
      </head>
      <body>
        <div class="statement">
          <div class="header">
            ${brandingLogo 
              ? `<img src="${brandingLogo}" alt="Logo" class="logo">`
              : `<div class="company-name">${brandingName}</div>`
            }
            ${brandingTagline ? `<div class="tagline">${brandingTagline}</div>` : ''}
          </div>
          
          <div class="meta-wrapper">
            <table class="meta-table">
              <tr>
                <td>
                  <span class="meta-label">Statement #</span>
                  <span class="meta-value">${statement.statement_number}</span>
                </td>
                <td>
                  <span class="meta-label">Date</span>
                  <span class="meta-value">${formatDate(statement.statement_date)}</span>
                </td>
                <td>
                  <span class="meta-label">Invoices</span>
                  <span class="meta-value">${statement.invoice_summaries?.length || 0}</span>
                </td>
                <td>
                  <span class="meta-label">Balance Due</span>
                  <span class="meta-value" style="color: ${statement.total_balance_due > 0 ? primaryColor : '#16a34a'};">
                    ${statement.total_balance_due <= 0 ? 'Paid' : formatCurrency(statement.total_balance_due)}
                  </span>
                </td>
              </tr>
            </table>
          </div>
          
          <div class="content">
            <div class="bill-to">
              <div class="bill-to-label">Bill To</div>
              <div class="bill-to-name">${statement.client?.name || ''}</div>
              ${statement.client?.email ? `<div class="bill-to-detail">${statement.client.email}</div>` : ''}
            </div>
            
            <div class="section-title">Invoices</div>
            <table class="invoices-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th style="text-align: center; width: 80px;">Date</th>
                  <th style="text-align: center; width: 80px;">Due Date</th>
                  <th style="text-align: right; width: 80px;">Total</th>
                  <th style="text-align: right; width: 80px;">Paid</th>
                  <th style="text-align: right; width: 80px;">Balance</th>
                </tr>
              </thead>
              <tbody>
                ${invoicesHTML}
              </tbody>
            </table>
            
            <div class="totals-wrapper">
              <div class="totals-section">
                <div class="total-row">
                  <span>Total Invoiced</span>
                  <span>${formatCurrency(statement.total_invoiced)}</span>
                </div>
                <div class="total-row">
                  <span>Total Paid</span>
                  <span>${formatCurrency(statement.total_paid)}</span>
                </div>
                ${statement.statement_paid > 0 ? `
                  <div class="total-row">
                    <span>Statement Payments</span>
                    <span style="color: #16a34a;">${formatCurrency(statement.statement_paid)}</span>
                  </div>
                ` : ''}
                <div class="total-row final">
                  <span>Balance Due</span>
                  <span class="amount" style="color: ${statement.total_balance_due > 0 ? primaryColor : '#16a34a'};">
                    ${statement.total_balance_due <= 0 ? 'Paid' : formatCurrency(statement.total_balance_due)}
                  </span>
                </div>
              </div>
            </div>
            
            ${statement.payments && statement.payments.length > 0 ? `
              <div class="payments-section">
                <div class="payments-header">Payments Received on Statement</div>
                ${statement.payments.map(p => `
                  <div class="payment-row">
                    <span>${formatDateShort(p.payment_date)} (${p.payment_method}${p.notes ? `: ${p.notes}` : ''})</span>
                    <span class="amount">-${formatCurrency(p.amount)}</span>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
          
          ${(footerPhone || footerEmail || footerWebsite || footerText) ? `
            <div class="footer">
              ${(footerPhone || footerEmail || footerWebsite) ? `
                <div class="footer-contact">
                  ${footerPhone ? `<span>${footerPhone}</span>` : ''}
                  ${footerEmail ? `<span>${footerEmail}</span>` : ''}
                  ${footerWebsite ? `<span>${footerWebsite}</span>` : ''}
                </div>
              ` : ''}
              <div class="footer-text">${footerText || 'Thank you for your business'}</div>
            </div>
          ` : ''}
        </div>
      </body>
      </html>
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatePrintHTML());
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleDownload = async () => {
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      
      const container = document.createElement('div');
      container.innerHTML = generatePrintHTML();
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);
      
      const element = container.querySelector('.statement');
      const filename = `Statement ${statement.statement_number}.pdf`;
      
      const opt = {
        margin: [0.35, 0.4, 0.35, 0.4],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          logging: false,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      await html2pdf().set(opt).from(element).save();
      document.body.removeChild(container);
      toast.success('Statement downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to download. Using print dialog instead.');
      handlePrint();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto p-4 md:p-6">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-white flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm md:text-base">
              Statement {statement.statement_number}
              {statement.status === 'paid' && (
                <span className="text-green-400 ml-2">| Paid</span>
              )}
            </span>
            <Badge className={`${statusConfig[statement.status]?.color || 'bg-slate-500'} text-white text-xs`}>
              {statusConfig[statement.status]?.label || statement.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Statement Content - Matching InvoiceViewDialog structure */}
        <div 
          ref={printRef} 
          className="bg-white rounded-lg overflow-hidden print:shadow-none"
          style={{ fontFamily: fontFamilyStyle, color: textColor }}
        >
          {/* Header - Clean centered logo with matching width */}
          <div className="px-5 md:px-8 pt-4 pb-2">
            <div 
              className="rounded-lg py-4 text-center"
              style={{ backgroundColor: headerBgColor }}
            >
              {brandingLogo ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={brandingLogo} 
                    alt="Logo" 
                    className={`${logoSizeClasses[logoSize] || logoSizeClasses.medium} object-contain`}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {brandingTagline && <p className="text-xs md:text-sm mt-2" style={{ color: textColor, opacity: 0.6 }}>{brandingTagline}</p>}
                </div>
              ) : (
                <div>
                  <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ color: primaryColor }}>{brandingName}</h1>
                  {brandingTagline && <p className="text-xs md:text-sm mt-1" style={{ color: textColor, opacity: 0.6 }}>{brandingTagline}</p>}
                </div>
              )}
            </div>
          </div>
          
          {/* Statement Meta Bar - Core info with dividers */}
          <div className="px-5 md:px-8 py-2">
            <div 
              className="rounded-lg border py-2"
              style={{ backgroundColor: headerBgColor, borderColor: accentColor + '40' }}
            >
              <div className="flex justify-between items-center text-center">
                <div className="flex-1">
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Statement #</p>
                  <p className="font-mono font-semibold text-[11px] md:text-xs">{statement.statement_number}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Date</p>
                  <p className="font-semibold text-[11px] md:text-xs">{formatDate(statement.statement_date)}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Invoices</p>
                  <p className="font-semibold text-[11px] md:text-xs">{statement.invoice_summaries?.length || 0}</p>
                </div>
                <div className="flex-1 border-l" style={{ borderColor: accentColor + '40' }}>
                  <p className="text-[9px] md:text-[10px] uppercase tracking-wider" style={{ color: textColor, opacity: 0.5 }}>Balance Due</p>
                  <p className="font-semibold text-[11px] md:text-xs" style={{ color: statement.total_balance_due > 0 ? primaryColor : '#16a34a' }}>
                    {statement.total_balance_due <= 0 ? 'Paid' : formatCurrency(statement.total_balance_due)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="px-5 md:px-8 py-4 md:py-5">
            {/* Bill To */}
            <div className="mb-6">
              <p className="text-[10px] md:text-xs uppercase tracking-wider font-medium mb-2" style={{ color: textColor, opacity: 0.5 }}>Bill To</p>
              <div>
                <p className="font-semibold text-sm md:text-base">{statement.client?.name}</p>
                {statement.client?.email && (
                  <p className="text-xs md:text-sm" style={{ color: accentColor }}>{statement.client.email}</p>
                )}
              </div>
            </div>

            {/* Invoices Header */}
            <div className="border-b-2 pb-2 mb-3" style={{ borderColor: primaryColor }}>
              <p className="text-xs md:text-sm font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>Invoices</p>
            </div>

            {/* Invoices Table - Desktop */}
            <div className="hidden md:block">
              <table className="w-full mb-4">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase">
                    <th className="text-left py-2 font-medium">Invoice</th>
                    <th className="text-center py-2 font-medium w-24">Date</th>
                    <th className="text-center py-2 font-medium w-24">Due Date</th>
                    <th className="text-right py-2 font-medium w-24">Total</th>
                    <th className="text-right py-2 font-medium w-24">Paid</th>
                    <th className="text-right py-2 font-medium w-24">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {statement.invoice_summaries?.map((inv) => (
                    <tr key={inv.invoice_id} className={inv.balance_due <= 0 ? 'bg-green-50/50' : ''}>
                      <td className="py-3">
                        <a 
                          href={`/invoice/${inv.invoice_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium hover:underline"
                          style={{ color: accentColor }}
                        >
                          {inv.invoice_number}
                        </a>
                      </td>
                      <td className="py-3 text-center text-slate-600">{formatDateShort(inv.invoice_date)}</td>
                      <td className="py-3 text-center text-slate-600">{formatDateShort(inv.due_date)}</td>
                      <td className="py-3 text-right text-slate-800">{formatCurrency(inv.total)}</td>
                      <td className="py-3 text-right text-slate-600">{formatCurrency(inv.total_paid)}</td>
                      <td className="py-3 text-right font-semibold">
                        <span className={inv.balance_due <= 0 ? 'text-green-600' : 'text-slate-800'}>
                          {inv.balance_due <= 0 ? 'Paid' : formatCurrency(inv.balance_due)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          
            {/* Invoices - Mobile Cards */}
            <div className="md:hidden space-y-2 mb-4">
              {statement.invoice_summaries?.map((inv) => (
                <div key={inv.invoice_id} className={`rounded p-3 ${inv.balance_due <= 0 ? 'bg-green-50' : 'bg-slate-50'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <a 
                      href={`/invoice/${inv.invoice_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-sm hover:underline"
                      style={{ color: accentColor }}
                    >
                      {inv.invoice_number}
                    </a>
                    <span className={`font-semibold text-sm ${inv.balance_due <= 0 ? 'text-green-600' : 'text-slate-800'}`}>
                      {inv.balance_due <= 0 ? 'Paid' : formatCurrency(inv.balance_due)}
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-500">
                    <span>Date: {formatDateShort(inv.invoice_date)}</span>
                    <span>Total: {formatCurrency(inv.total)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals Section */}
            <div className="border-t border-slate-200 pt-3">
              <div className="flex justify-end">
                <div className="w-full md:w-64">
                  <div className="flex justify-between py-1.5 text-xs md:text-sm">
                    <span className="text-slate-500">Total Invoiced</span>
                    <span className="text-slate-700">{formatCurrency(statement.total_invoiced)}</span>
                  </div>
                  <div className="flex justify-between py-1.5 text-xs md:text-sm">
                    <span className="text-slate-500">Total Paid</span>
                    <span className="text-slate-700">{formatCurrency(statement.total_paid)}</span>
                  </div>
                  {statement.statement_paid > 0 && (
                    <div className="flex justify-between py-1.5 text-xs md:text-sm">
                      <span className="text-slate-500">Statement Payments</span>
                      <span className="text-green-600">{formatCurrency(statement.statement_paid)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 mt-1 border-t-2 border-slate-800">
                    <span className="font-bold text-sm md:text-base text-slate-800">Balance Due</span>
                    <span className={`font-bold text-base md:text-lg ${statement.total_balance_due > 0 ? '' : 'text-green-600'}`} style={{ color: statement.total_balance_due > 0 ? primaryColor : undefined }}>
                      {statement.total_balance_due <= 0 ? 'Paid' : formatCurrency(statement.total_balance_due)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment History */}
            {statement.payments && statement.payments.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider font-medium mb-2">Payments Received on Statement</p>
                <div className="space-y-2">
                  {statement.payments.map((payment, idx) => (
                    <div key={payment.id || idx} className="bg-green-50 rounded-lg p-3 text-sm flex justify-between items-center">
                      <div>
                        <span className="text-slate-600">{formatDateShort(payment.payment_date)}</span>
                        <span className="text-slate-500 ml-2">({payment.payment_method}{payment.notes ? `: ${payment.notes}` : ''})</span>
                      </div>
                      <span className="text-green-600 font-medium">-{formatCurrency(payment.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {statement.notes && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Notes</p>
                <p className="text-slate-600 whitespace-pre-wrap text-xs md:text-sm">{statement.notes}</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-slate-50 px-5 md:px-8 py-3 border-t border-slate-200 print:bg-white">
            {(footerPhone || footerEmail || footerWebsite) && (
              <div className="flex justify-center items-center gap-6 text-[10px] md:text-xs text-slate-500 mb-2">
                {footerPhone && <span>{footerPhone}</span>}
                {footerEmail && <span>{footerEmail}</span>}
                {footerWebsite && <span>{footerWebsite}</span>}
              </div>
            )}
            <p className="text-[10px] md:text-xs text-slate-400 text-center">
              {footerText || 'Thank you for your business'}
            </p>
          </div>
        </div>

        {/* Actions - Matching InvoiceViewDialog style */}
        <div className="flex flex-wrap justify-center gap-1.5 mt-4">
          {statement.status !== 'paid' && (
            <Button 
              onClick={() => {
                setPaymentAmount(statement.total_balance_due.toString());
                setShowPaymentDialog(true);
              }} 
              className="bg-green-600 hover:bg-green-700 text-xs h-8 px-3"
            >
              Record Payment
            </Button>
          )}
          <Button onClick={handlePrint} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3">
            Print
          </Button>
          <Button onClick={handleDownload} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3">
            Download
          </Button>
          <Button onClick={handleCopyLink} variant="outline" className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3">
            Copy Link
          </Button>
          <Button 
            onClick={() => window.open(`/statement/${statement.id}`, '_blank')} 
            variant="outline" 
            className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs h-8 px-3"
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            View Public
          </Button>
        </div>
      </DialogContent>

      {/* Record Payment Dialog - Matching InvoiceViewDialog style */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <p className="text-slate-400 text-sm">Balance Due</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(statement.total_balance_due)}</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {PAYMENT_METHODS.map(method => (
                    <SelectItem key={method.value} value={method.value} className="text-white">
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Notes (optional)</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="e.g., Check #1234, Transaction ID"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowPaymentDialog(false)}
                className="flex-1 border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRecordPayment}
                disabled={isRecordingPayment}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {isRecordingPayment ? 'Recording...' : 'Record Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
